/**
 * RestSyncTarget — a concrete, vendor-neutral cloud adapter for the SyncTarget
 * port. It speaks a small, standard HTTP contract so it works against your own
 * Hono/Express API on a VPS, a PostgREST/Supabase endpoint (with a thin view),
 * or any service that implements four routes — no provider lock-in:
 *
 *   GET    {base}/sync/{entityId}   -> 200 SyncRecord | 404
 *   PUT    {base}/sync/{entityId}   <- SyncRecord body, -> 200
 *   GET    {base}/sync?since={iso}  -> 200 SyncRecord[]
 *
 * Auth is a bearer token (JWT/OIDC/anon key — caller's choice). `fetch` is
 * injectable, so this adapter is unit-testable with an in-memory fake and needs
 * no real network to verify its behaviour.
 */

import type { EntityId } from '@life-os/app-core';
import type { RemoteRecordSnapshot } from './mutation.js';
import type { SyncRecord, SyncTarget } from './sync.js';

export interface HttpResponse {
  readonly ok: boolean;
  readonly status: number;
  json(): Promise<unknown>;
}

export type FetchLike = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<HttpResponse>;

export interface RestSyncOptions {
  readonly baseUrl: string;
  readonly token?: string | null;
  /** Injectable for tests; defaults to the global fetch. */
  readonly fetchImpl?: FetchLike;
}

export class RestSyncTarget implements SyncTarget {
  readonly name = 'rest';
  private readonly base: string;
  private readonly token: string | null;
  private readonly doFetch: FetchLike;

  constructor(opts: RestSyncOptions) {
    this.base = opts.baseUrl.replace(/\/$/, '');
    this.token = opts.token ?? null;
    const injected = opts.fetchImpl;
    if (injected) {
      this.doFetch = injected;
    } else if (typeof globalThis.fetch === 'function') {
      this.doFetch = (url, init) => globalThis.fetch(url, init) as unknown as Promise<HttpResponse>;
    } else {
      throw new Error('RestSyncTarget: no fetch available; pass fetchImpl.');
    }
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = { 'content-type': 'application/json' };
    if (this.token) h.authorization = `Bearer ${this.token}`;
    return h;
  }

  async get(entityId: EntityId): Promise<SyncRecord | null> {
    const res = await this.doFetch(`${this.base}/sync/${encodeURIComponent(String(entityId))}`, {
      method: 'GET',
      headers: this.headers(),
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Sync get failed: ${res.status}`);
    return (await res.json()) as SyncRecord;
  }

  async head(entityId: EntityId): Promise<RemoteRecordSnapshot | null> {
    const record = await this.get(entityId);
    return record ? { entityId, version: record.version } : null;
  }

  async push(record: SyncRecord): Promise<void> {
    const res = await this.doFetch(`${this.base}/sync/${encodeURIComponent(String(record.entityId))}`, {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify(record),
    });
    if (!res.ok) throw new Error(`Sync push failed: ${res.status}`);
  }

  async pull(since: string | null): Promise<readonly SyncRecord[]> {
    const qs = since === null ? '' : `?since=${encodeURIComponent(since)}`;
    const res = await this.doFetch(`${this.base}/sync${qs}`, { method: 'GET', headers: this.headers() });
    if (!res.ok) throw new Error(`Sync pull failed: ${res.status}`);
    const body = (await res.json()) as SyncRecord[];
    return Array.isArray(body) ? body : [];
  }
}

/** Build a RestSyncTarget from config, or null when no sync endpoint is set. */
export function restSyncTargetFromConfig(
  cfg: { syncApiUrl: string | null },
  token?: string | null,
  fetchImpl?: FetchLike,
): RestSyncTarget | null {
  if (!cfg.syncApiUrl) return null;
  return new RestSyncTarget({ baseUrl: cfg.syncApiUrl, token: token ?? null, fetchImpl });
}
